import { AgGridAngular } from '@ag-grid-community/angular';
import {
  CellClassParams,
  GridApi,
  GridOptions,
  GridReadyEvent,
  ICellRendererParams,
  IRowNode,
  ModelUpdatedEvent,
  RowNode,
  RowSelectedEvent,
  SortModelItem,
  ValueFormatterParams,
} from '@ag-grid-community/core';
import { AsyncPipe, DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { isEqual, isNumber, pick } from 'lodash-es';
import { BehaviorSubject } from 'rxjs';

import { SitesQuery } from '@models/sites/sites.query';

import { AgActionsComponent } from '@shared/ag-components/ag-actions/ag-actions.component';
import { AgDropdownComponent, AgInputComponent } from '@shared/ag-components/ag-controls';
import { AgLoadingCellComponent } from '@shared/ag-components/ag-loading-cell/ag-loading-cell.component';
import { PaginationPanelComponent } from '@shared/components/pagination-panel/pagination-panel.component';
import { LocalStorageKey } from '@shared/constants/localStorageKey';
import {
  DEFAULT_PAGINATION_SIZE,
  initialPaginationSize,
  setPaginationSize,
} from '@shared/constants/pagination.constants';
import { TableConstants } from '@shared/constants/table.constants';
import { StickyGridDirective } from '@shared/directives/sticky-grid/sticky-grid.directive';
import { ApiService, AwsFile } from '@shared/services/api.service';
import {
  DocumentLibraryFile,
  DocumentLibraryService,
} from '@shared/services/document-library.service';
import { Document, DocumentType, SortField } from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { FilterEntity, TableFilter, TableService } from '@shared/services/table.service';
import { AuthQuery } from '@shared/store/auth/auth.query';
import { AuxExcelStyleKeys, GetExcelStyle } from '@shared/utils';
import { Utils } from '@shared/utils/utils';

import { DocumentLibraryConstants } from '../document-library.constants';
import { DocumentsPageService } from '../documents-page.service';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AgGridAngular, StickyGridDirective, PaginationPanelComponent, AsyncPipe],
  selector: 'aux-document-library',
  styleUrls: ['./document-library.component.scss'],
  templateUrl: './document-library.component.html',
})
export class DocumentLibraryComponent implements OnInit {
  documentsPageService = inject(DocumentsPageService);

  private apiService = inject(ApiService);
  private authQuery = inject(AuthQuery);
  private destroyRef = inject(DestroyRef);
  private documentLibraryService = inject(DocumentLibraryService);
  private fb = inject(UntypedFormBuilder);
  private overlayService = inject(OverlayService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private sitesQuery = inject(SitesQuery);

  @Output() deleteRowEvent = new EventEmitter<string>();
  disabledDeleteButton$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  @Input() doesExternalFilterPass: ((node: IRowNode<Document>) => boolean) | undefined;
  enabledDeleteButton$: BehaviorSubject<boolean> = new BehaviorSubject(true);
  @Output() filterChange = new EventEmitter<
    Pick<DocumentLibraryFile, 'description' | 'document_type_id' | 'id' | 'site_id' | 'vendor_id'>[]
  >();
  @Input() formGroup!: UntypedFormGroup;
  gridAPI!: GridApi;
  gridData$ = new BehaviorSubject<Document[]>([]);
  gridOptions$ = new BehaviorSubject<GridOptions>({});
  @Input() inputChangeHandler!: () => void;
  @Input() isExternalFilterPresent: (() => boolean) | undefined;
  loadingCellRenderer = AgLoadingCellComponent;
  overlayNoRowsTemplate = TableConstants.NO_ROWS_MESSAGE;
  prevFilters: Record<string, TableFilter> = {};
  @Input() removedRows$!: BehaviorSubject<string[]>;
  @Output() rollbackDeleteRowEvent = new EventEmitter<RowNode>();
  @Output() rowDataChangedEvent = new EventEmitter();
  @Input() rowSelectedHandler!: (event?: RowSelectedEvent<Document>) => void;
  totalItems$ = new BehaviorSubject(10);

  private controlCellClassNames = 'flex flex-col justify-center !items-stretch';
  private nonDeletableDocuments = [
    DocumentType.DOCUMENT_AUDIT_EVIDENCE,
    DocumentType.DOCUMENT_VENDOR_BUDGET,
    DocumentType.DOCUMENT_SITE_BUDGET,
    DocumentType.DOCUMENT_VENDOR_ESTIMATE,
    DocumentType.DOCUMENT_MANUAL_TRANSACTION_SUPPORT,
    DocumentType.DOCUMENT_PO_REPORT,
  ];

  // eslint-disable-next-line max-lines-per-function
  ngOnInit() {
    this.gridOptions$.next({
      ...TableConstants.DEFAULT_GRID_OPTIONS.GRID_OPTIONS,
      columnDefs: [
        {
          cellClass: this.getRowClassesDeleteIndicatorRow([]),
          checkboxSelection: true,
          headerCheckboxSelection: true,
          maxWidth: 35,
          minWidth: 35,
          width: 35,
        },
        {
          cellClass: this.getRowClassesDeleteIndicatorRow(['cell-justify-center']),
          cellRendererParams: (params: ICellRendererParams) => {
            return {
              deleteButtonDisabled$:
                this.nonDeletableDocuments.includes(params?.data?.document_type_id) &&
                !params?.data?.is_metadata_editable
                  ? this.enabledDeleteButton$
                  : this.disabledDeleteButton$,
              deleteClickFN: ({ rowNode }: { rowNode: RowNode }) => {
                if (!this.removedRows$.getValue().includes(rowNode.data.id)) {
                  this.deleteRowEvent.emit(rowNode.data.id);
                } else {
                  this.rollbackDeleteRowEvent.emit(rowNode);
                }

                this.gridAPI.redrawRows({ rowNodes: [rowNode] });
              },
              deletedRows: this.removedRows$,
              downloadClickFN: async ({ rowNode }: { rowNode: RowNode }) => {
                const ref = this.overlayService.loading();

                await this.apiService.downloadFile({
                  fileName: rowNode.data.name,
                  key: rowNode.data.bucket_key.replace('public/', ''),
                } as AwsFile);

                ref.close();
              },
              hideDownloadButton: false,
              hideEditButton: true,
            };
          },
          cellRendererSelector: (params) => ({ component: AgActionsComponent, params }),
          field: 'actions',
          headerName: '',
          maxWidth: 75,
          suppressMenu: true,
        },
        {
          cellClass: this.getRowClassesDeleteIndicatorRow([this.controlCellClassNames]),
          cellRenderer: AgInputComponent,
          cellRendererParams: {
            changeHandler: this.inputChangeHandler,
            formGroup: this.formGroup,
            placeholder: 'Description',
          },
          field: 'description',
          headerClass: 'ag-header-align-center font-bold',
          headerName: 'Document Short Name',
          tooltipField: 'description',
        },
        {
          cellClass: this.getRowClassesDeleteIndicatorRow(['text-left']),
          cellRenderer: Utils.getCellWrapper('ag-cell-value', 'value'),
          field: 'name',
          headerClass: 'ag-header-align-center font-bold',
          headerName: 'File Name',
          tooltipField: 'name',
          valueGetter: (params) => params.data?.name || '',
        },
        {
          cellClass: this.getRowClassesDeleteIndicatorRow([this.controlCellClassNames]),
          cellRenderer: AgDropdownComponent,
          cellRendererParams: {
            changeHandler: this.inputChangeHandler,
            disableTooltip: DocumentLibraryConstants.DISABLED_INPUT_TOOLTIP,
            options: this.documentLibraryService.getDocumentOptions(),
          },
          field: 'document_type_id',
          getQuickFilterText: (params) =>
            Utils.getOptionLabel(
              this.documentLibraryService.getDocumentOptions(),
              params?.data?.document_type_id
            ),
          headerClass: 'ag-header-align-center font-bold',
          headerName: 'Document Type',
          tooltipValueGetter: (params) =>
            Utils.getOptionLabel(
              this.documentLibraryService.getDocumentOptions(),
              params?.data?.document_type_id
            ),
        },
        {
          cellClass: this.getRowClassesDeleteIndicatorRow([this.controlCellClassNames]),
          cellRenderer: AgDropdownComponent,
          cellRendererParams: {
            changeHandler: this.inputChangeHandler,
            disableTooltip: DocumentLibraryConstants.DISABLED_INPUT_TOOLTIP,
            options: this.documentsPageService.vendors,
          },
          field: 'vendor_id',
          getQuickFilterText: (params) =>
            Utils.getOptionLabel(this.documentsPageService.vendors, params?.data?.vendor_id),
          headerClass: 'ag-header-align-center font-bold',
          headerName: 'Vendor Contract',
          tooltipValueGetter: (params) =>
            Utils.getOptionLabel(this.documentsPageService.vendors, params?.data?.vendor_id),
        },
        {
          cellClass: this.getRowClassesDeleteIndicatorRow([this.controlCellClassNames]),
          cellRenderer: AgDropdownComponent,
          cellRendererParams: {
            bindLabel: 'siteName',
            changeHandler: this.inputChangeHandler,
            disableTooltip: DocumentLibraryConstants.DISABLED_INPUT_TOOLTIP,
            options: this.sitesQuery.selectSiteOptions()(),
          },
          field: 'site_id',
          getQuickFilterText: (params) =>
            Utils.getOptionLabel(this.documentsPageService.sites, params?.data?.site_id),
          headerClass: 'ag-header-align-center font-bold',
          headerName: 'Site',
          tooltipValueGetter: (params) =>
            Utils.getOptionLabel(this.documentsPageService.sites, params?.data?.site_id),
        },
        {
          cellClass: this.getRowClassesDeleteIndicatorRow(['text-right']),
          cellRenderer: Utils.getCellWrapper('ag-cell-value', 'valueFormatted'),
          field: 'create_date',
          headerClass: 'ag-header-align-center font-bold',
          headerName: 'Date Uploaded',
          maxWidth: 180,
          tooltipValueGetter: (params) =>
            new DatePipe('en-US').transform(params?.data?.create_date, 'MM/dd/yyyy, h:mm a'),
          valueFormatter: (val: ValueFormatterParams) => {
            return new DatePipe('en-US').transform(val.value, 'MM/dd/yyyy, h:mm a') || '';
          },
        },
        {
          cellClass: this.getRowClassesDeleteIndicatorRow(['text-left']),
          cellRenderer: Utils.getCellWrapper('ag-cell-value', 'valueFormatted'),
          field: 'created_by',
          headerClass: 'ag-header-align-center font-bold',
          headerName: 'Uploaded by',
          tooltipValueGetter: (params) =>
            Utils.agUserFormatter(params?.data?.updatedByName, this.authQuery.isAuxAdmin()),
          valueFormatter: (params) =>
            Utils.agUserFormatter(params?.data?.updatedByName, this.authQuery.isAuxAdmin()),
        },
      ],
      defaultColDef: {
        ...TableConstants.DEFAULT_GRID_OPTIONS.DEFAULT_COL_DEF,
        resizable: true,
      },
      doesExternalFilterPass: this.doesExternalFilterPass,
      excelStyles: [
        GetExcelStyle(AuxExcelStyleKeys.HEADER),
        GetExcelStyle(AuxExcelStyleKeys.FIRST_ROW),
        {
          alignment: { horizontal: 'Left' },
          font: { fontName: 'Arial', size: 11 },
          id: 'cell',
        },
      ],
      isExternalFilterPresent: this.isExternalFilterPresent,
      isRowSelectable: (node) => !!node?.data?.is_metadata_editable,
      onModelUpdated: (event: ModelUpdatedEvent) => {
        if (event.api.getDisplayedRowCount() === 0) {
          event.api.showNoRowsOverlay();
        } else {
          event.api.hideOverlay();
        }

        if (this.gridData$.getValue().length) {
          this.overlayNoRowsTemplate = TableConstants.NO_ROWS_ON_FILTERING_MESSAGE;
        }
      },
      pagination: true,
      paginationPageSize: initialPaginationSize(LocalStorageKey.PAGINATION_DOCUMENT_LIBRARY),
      rowHeight: 45,
      rowModelType: 'serverSide',
      suppressPaginationPanel: true,
    });

    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      if (params['documentType']) {
        const documentTypesControl = this.formGroup.get('documentTypes');

        if (documentTypesControl) {
          documentTypesControl.setValue([params['documentType']]);
          void this.router.navigate([], { queryParams: {}, replaceUrl: true });
        }
      }

      if (params['site']) {
        const sitesControl = this.formGroup.get('sites');

        if (sitesControl) {
          sitesControl.setValue([params['site']]);
          void this.router.navigate([], { queryParams: {}, replaceUrl: true });
        }
      }

      if (params['dateFrom']) {
        const dateFromControl = this.formGroup.get('dateFrom');

        if (dateFromControl) {
          dateFromControl.setValue(params['dateFrom']);
          void this.router.navigate([], { queryParams: {}, replaceUrl: true });
        }
      }

      if (params['dateTo']) {
        const dateToControl = this.formGroup.get('dateTo');

        if (dateToControl) {
          dateToControl.setValue(params['dateTo']);
          void this.router.navigate([], { queryParams: {}, replaceUrl: true });
        }
      }
    });
  }

  private createFormControls(data: DocumentLibraryFile[], resetFormBeforeReinitialize: boolean) {
    if (resetFormBeforeReinitialize) {
      const tableFormControl = this.formGroup.controls.table as UntypedFormArray;

      if (tableFormControl.controls) {
        tableFormControl.clear();
      }
    }

    const gridRows = ((this.formGroup.controls?.table as UntypedFormArray)?.controls ||
      []) as AbstractControl[];

    const formArray = new UntypedFormArray(gridRows);

    data.forEach(
      ({ description, document_type_id, id, is_metadata_editable, site_id, vendor_id }) => {
        formArray.push(
          this.fb.group({
            description: new UntypedFormControl({
              disabled: false,
              value: description || '',
            }),
            document_type_id: new UntypedFormControl({
              disabled: !is_metadata_editable,
              value: document_type_id,
            }),
            id,
            site_id: new UntypedFormControl({ disabled: !is_metadata_editable, value: site_id }),
            vendor_id: new UntypedFormControl({
              disabled: !is_metadata_editable,
              value: vendor_id,
            }),
          })
        );
      }
    );

    this.formGroup.setControl('table', formArray);
  }

  getGridContext() {
    return {
      formGroup: this.formGroup,
    };
  }

  private getRowClassesDeleteIndicatorRow =
    (classes: string[]) =>
    (cellClassParams: CellClassParams): string[] => {
      const markedToDeleteRow = this.removedRows$.getValue().includes(cellClassParams?.data?.id);

      return markedToDeleteRow ? [...classes, 'removed-row'] : classes;
    };

  getSortingModel(sortModel: SortModelItem[]): SortField[] {
    return sortModel.map(({ colId, sort }) => ({
      descending: sort === 'desc',
      field: colId,
    }));
  }

  // eslint-disable-next-line max-lines-per-function
  onGridReady({ api }: GridReadyEvent) {
    this.gridAPI = api;

    this.gridAPI.setGridOption('serverSideDatasource', {
      getRows: (params) => {
        this.resetTableStateBeforePagination();

        const { dateFrom, dateTo, documentTypes, search, sites, vendors } =
          this.formGroup.getRawValue();

        const filterModel: Record<string, FilterEntity> = {
          create_date: {
            type: 'date',
            value: [dateFrom, dateTo],
          },
          document_type_id: {
            type: 'text',
            value: documentTypes,
          },
          site_id: {
            type: 'text',
            value: sites,
          },
          vendor_id: {
            type: 'text',
            value: vendors,
          },
        };

        const filters = TableService.getServerSideFilters(filterModel);

        this.documentsPageService
          .getDocumentsWithPagination({
            end_row: params.request.endRow || 100,
            filter_model: JSON.stringify(filters),
            search_text: search,
            sort_model: this.getSortingModel(params.request.sortModel),
            start_row: params.request.startRow || 0,
          })
          .subscribe((gridData) => {
            if (gridData.data) {
              if (isNumber(gridData.totalItems)) {
                this.totalItems$.next(gridData.totalItems);
              }

              const allGridData =
                params.request.startRow === 0
                  ? gridData.data
                  : this.gridData$.getValue().concat(gridData.data);

              this.gridData$.next(allGridData);
              params.success({ rowCount: this.totalItems$.getValue(), rowData: gridData.data });
              this.gridAPI?.hideOverlay();
            } else {
              params.success({ rowCount: 0, rowData: [] });
              this.gridData$.next([]);
              this.gridAPI?.showNoRowsOverlay();
            }

            const noFilterChange = isEqual(this.prevFilters, filters);

            this.refreshFormControls(gridData.data, !noFilterChange);

            if (!noFilterChange) {
              this.prevFilters = { ...filters };

              const list = gridData.data.map((file) => {
                const transformFile = pick(file, [
                  'description',
                  'document_type_id',
                  'vendor_id',
                  'site_id',
                  'id',
                ]);

                transformFile.description = transformFile.description || '';

                return transformFile;
              });

              this.filterChange.emit(list);
            }
          });
      },
    });

    this.gridAPI.sizeColumnsToFit();
  }

  paginationChange() {
    this.gridAPI.deselectAll();
    setPaginationSize(
      LocalStorageKey.PAGINATION_DOCUMENT_LIBRARY,
      this.gridAPI?.paginationGetPageSize() || DEFAULT_PAGINATION_SIZE
    );
  }

  refreshFormControls(data: DocumentLibraryFile[], resetFormBeforeReinitialize: boolean) {
    if (this.gridAPI) {
      this.createFormControls(data, resetFormBeforeReinitialize);
      this.gridAPI.refreshCells({ force: true });
    }
  }

  private resetTableStateBeforePagination() {
    this.gridAPI.deselectAll();
    this.rowSelectedHandler();
  }
}
